import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, X, Check } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", theme: "", tagline: "", banner_image: "", start_at: "", end_at: "", invited_vendor_ids: [] };
const DURATION_PRESETS = [3, 6, 9, 12];

function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminDropsTab() {
  const [drops, setDrops] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [subsFor, setSubsFor] = useState(null); // drop object for submissions modal
  const [subs, setSubs] = useState([]);
  const [summaryFor, setSummaryFor] = useState(null);

  const reload = async () => {
    const [d, v] = await Promise.all([api.get("/admin/drops"), api.get("/admin/vendors")]);
    setDrops(d.data || []); setVendors(v.data || []);
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.start_at || !form.end_at) return toast.error("Name, start and end required");
    try {
      const payload = { ...form, start_at: new Date(form.start_at).toISOString(), end_at: new Date(form.end_at).toISOString() };
      await api.post("/admin/drops", payload); toast.success("Drop created"); setOpen(false); setForm(empty); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  const extend = async (id) => {
    const val = window.prompt("New end date/time (YYYY-MM-DD HH:mm)?");
    if (!val) return;
    try { await api.patch(`/admin/drops/${id}`, { end_at: new Date(val).toISOString() }); toast.success("End time updated"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const endNow = async (id) => { if (!window.confirm("End this drop now?")) return; await api.post(`/admin/drops/${id}/end`); toast.success("Drop ended"); reload(); };
  const remove = async (id) => { if (!window.confirm("Delete drop + all submissions?")) return; await api.delete(`/admin/drops/${id}`); toast.success("Deleted"); reload(); };
  const openSubs = async (d) => { setSubsFor(d); const r = await api.get(`/admin/drops/${d.id}/submissions`); setSubs(r.data || []); };
  const decide = async (drop, sub, decision) => {
    let priceOverride = null;
    if (decision === "approved") {
      const p = window.prompt(`Approve at drop price ₹?`, String(sub.drop_price));
      if (p == null) return;
      priceOverride = Number(p);
    }
    try {
      await api.post(`/admin/drops/${drop.id}/submissions/${sub.id}/decision`, { decision, drop_price_override: priceOverride });
      toast.success(`Submission ${decision}`);
      const r = await api.get(`/admin/drops/${drop.id}/submissions`); setSubs(r.data || []); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openSummary = async (d) => {
    try { const r = await api.get(`/admin/drops/${d.id}/summary`); setSummaryFor(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Summary failed"); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader(); rd.onload = () => setForm({ ...form, banner_image: rd.result }); rd.readAsDataURL(f);
  };
  const toggleVendor = (id) => setForm({ ...form, invited_vendor_ids: form.invited_vendor_ids.includes(id) ? form.invited_vendor_ids.filter((x) => x !== id) : [...form.invited_vendor_ids, id] });
  const applyDuration = (hours) => {
    const start = form.start_at ? new Date(form.start_at) : new Date();
    const end = new Date(start.getTime() + hours * 3600000);
    setForm({ ...form, start_at: form.start_at || toLocalInputValue(start), end_at: toLocalInputValue(end) });
  };

  return (
    <div className="space-y-6" data-testid="admin-drops-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl">Drop Events</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="drop-create-btn" className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]"><Plus className="h-4 w-4 mr-2" />New drop</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create drop</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Input data-testid="drop-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input placeholder="Theme (e.g. Diwali Nightwear)" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} />
              <Textarea placeholder="Tagline shown on banner" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} rows={2} />
              <div>
                <label className="text-[11px] text-gray-500">Quick duration (from start time)</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {DURATION_PRESETS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      data-testid={`drop-duration-${h}h`}
                      onClick={() => applyDuration(h)}
                      className="px-3 h-8 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      {h} hours
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[11px] text-gray-500">Start</label><Input data-testid="drop-start" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></div>
                <div><label className="text-[11px] text-gray-500">End</label><Input data-testid="drop-end" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></div>
              </div>
              <div>
                <label className="text-[11px] text-gray-500">Banner image <span className="text-gray-400">(recommended 1600 × 600 px, JPG/PNG under 500 KB)</span></label>
                <Input type="file" accept="image/*" onChange={onFile} />
              </div>
              <div>
                <div className="text-[11px] text-gray-500 mb-1">Invite vendors ({form.invited_vendor_ids.length} selected — leave empty to allow all)</div>
                <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 p-2 grid grid-cols-2 gap-1 text-xs">
                  {vendors.map((v) => (
                    <label key={v.id} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={form.invited_vendor_ids.includes(v.id)} onChange={() => toggleVendor(v.id)} className="accent-[#F48E2E]" />
                      <span className="truncate">{v.kyc?.business_name || v.full_name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="drop-submit" onClick={create} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Publish drop</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {drops.length === 0 && <div className="p-6 text-sm text-gray-500 bg-white rounded-2xl border">No drops yet.</div>}
        {drops.map((d) => (
          <div key={d.id} data-testid={`drop-row-${d.id}`} className="p-4 rounded-2xl bg-white border border-gray-200 flex flex-wrap gap-3 items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="font-display text-lg">{d.name}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${d.state === "live" ? "bg-green-100 text-green-700" : d.state === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-700"}`}>{d.state}</span>
              </div>
              <div className="text-xs text-gray-500">{d.theme}</div>
              <div className="text-xs text-gray-500 mt-1">{new Date(d.start_at).toLocaleString("en-IN")} → {new Date(d.end_at).toLocaleString("en-IN")}</div>
              <div className="text-xs text-gray-500 mt-1">Submissions: {d.submission_count} · Approved: {d.approved_count}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => openSubs(d)}>Submissions</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => extend(d.id)}>Extend</Button>
              {d.state !== "ended" && <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => endNow(d.id)}>End now</Button>}
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => openSummary(d)}>Summary</Button>
              <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => remove(d.id)}><X className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!subsFor} onOpenChange={(o) => !o && setSubsFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="drop-subs-dialog">
          <DialogHeader><DialogTitle>{subsFor?.name} · Submissions</DialogTitle></DialogHeader>
          {subs.length === 0 ? <div className="text-sm text-gray-500">No submissions yet.</div> : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {subs.map((s) => (
                <div key={s.id} className="p-3 rounded-xl border border-gray-200 flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="font-medium truncate">{s.product_name}</div>
                    <div className="text-xs text-gray-500">by {s.vendor_name || s.vendor_id.slice(0,8)} · ₹{s.drop_price} · Qty {s.quantity}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${s.status === "approved" ? "bg-green-100 text-green-700" : s.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-800"}`}>{s.status}</span>
                  {s.status === "pending" && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => decide(subsFor, s, "approved")} className="rounded-full bg-green-600 hover:bg-green-700 text-white h-8"><Check className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => decide(subsFor, s, "rejected")} className="rounded-full text-red-600 border-red-200 h-8"><X className="h-3 w-3" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!summaryFor} onOpenChange={(o) => !o && setSummaryFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="drop-summary-dialog">
          <DialogHeader><DialogTitle>{summaryFor?.drop?.name} · Summary</DialogTitle></DialogHeader>
          {summaryFor && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-gray-50"><div className="text-[11px] text-gray-500">Total revenue</div><div className="font-display text-2xl">₹{Number(summaryFor.revenue).toLocaleString("en-IN")}</div></div>
                <div className="p-3 rounded-xl bg-gray-50"><div className="text-[11px] text-gray-500">Units sold</div><div className="font-display text-2xl">{summaryFor.units_sold}</div></div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Top products</div>
                {summaryFor.top_products.length === 0 ? <div className="text-xs text-gray-500">No sales yet.</div> : summaryFor.top_products.map((p) => (
                  <div key={p.product_id} className="flex justify-between text-xs py-1 border-b border-gray-100"><span className="truncate">{p.product_name}</span><span>{p.units} · ₹{p.revenue}</span></div>
                ))}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Participating vendors</div>
                <div className="text-xs">{summaryFor.vendors.length} vendors · total units {summaryFor.vendors.reduce((a, v) => a + v.units, 0)}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
